import { ClipboardHeartIcon, PillIcon, AlertIcon, IconProps } from '../components/icons';
import { Tint } from './mock';
import React from 'react';

/* ---------- Care Plans (card-flows §3) ---------- */
/* Everything here is the user's own plan, agreed with their care team and
   authored in the admin web — the assistant finds it, it never invents it.
   The emergency steps follow Asthma + Lung UK guidance. */

export type PlanId = 'action-plan' | 'med-schedule' | 'emergency';
export type Plan = {
  id: PlanId;
  title: string;
  status: string;
  icon: (p: IconProps) => React.ReactElement;
  tint: Tint;
};

/* the plan picker — Emergency Steps is coral and ALWAYS last, so its
   position is predictable when someone needs it in a hurry */
export const PLANS: Plan[] = [{
  id: 'action-plan',
  title: 'Asthma Action Plan',
  status: 'Reviewed with your nurse · 12 May',
  icon: ClipboardHeartIcon,
  tint: 'sage'
}, {
  id: 'med-schedule',
  title: 'Medication Schedule',
  status: '2 medications · daily',
  icon: PillIcon,
  tint: 'sky'
}, {
  id: 'emergency',
  title: 'Emergency Steps',
  status: 'What to do in an attack',
  icon: AlertIcon,
  tint: 'coral'
}];

/* ---------- the asthma action plan (3 zones) ---------- */

export type ZoneId = 'green' | 'amber' | 'red';
export type Zone = {
  id: ZoneId;
  name: string;
  headline: string;
  tint: Tint;
  signs: string[];
  actions: string[];
};
export const ACTION_PLAN = {
  reviewed: 'Reviewed with your asthma nurse · 12 May',
  narrated: true,
  zones: [{
    id: 'green',
    name: 'Green zone',
    headline: "You're doing well",
    tint: 'sage',
    signs: ['No cough, wheeze or chest tightness', 'Sleeping through the night', 'Doing your usual activities'],
    actions: ['Take your preventer (the brown one) — 2 puffs, morning and evening', 'Keep your reliever (the blue one) with you', 'Carry on as normal']
  }, {
    id: 'amber',
    name: 'Amber zone',
    headline: 'Getting worse',
    tint: 'butter',
    signs: ['Coughing or wheezing more than usual', 'Waking at night with symptoms', 'Needing your reliever more than 3 times a week'],
    actions: ['Take 1–2 puffs of your reliever when symptoms come on', 'Keep taking your preventer every morning and evening', 'Book a GP or asthma-nurse appointment within 24 hours']
  }, {
    id: 'red',
    name: 'Red zone',
    headline: 'Emergency',
    tint: 'coral',
    signs: ['Too breathless to speak in full sentences', 'Reliever not helping, or not lasting 4 hours', 'Lips or fingertips looking blue'],
    actions: ['Follow your emergency steps now', "Call 999 if you're getting worse or not improving"]
  }] as Zone[]
};

/* ---------- the medication schedule ---------- */

export type ScheduledMed = {
  id: string;
  slot: string;
  name: string;
  kind: 'preventer' | 'reliever';
  dose: string;
  detail: string;
  taken: boolean | null; /* null = as-needed, no daily state */
};
export const MED_SCHEDULE = {
  summary: '2 medications · every day',
  meds: [{
    id: 'morning-preventer',
    slot: 'Morning',
    name: 'Clenil Modulite',
    kind: 'preventer',
    dose: '2 puffs',
    detail: 'Your preventer — the brown one, with breakfast',
    taken: true
  }, {
    id: 'evening-preventer',
    slot: 'Evening',
    name: 'Clenil Modulite',
    kind: 'preventer',
    dose: '2 puffs',
    detail: 'Your preventer — the brown one, before bed',
    taken: false
  }, {
    id: 'reliever',
    slot: 'As needed',
    name: 'Ventolin',
    kind: 'reliever',
    dose: '1–2 puffs',
    detail: 'Your reliever — the blue one, when symptoms come on',
    taken: null
  }] as ScheduledMed[]
};

/* ---------- the emergency steps ---------- */
/* Numbered, large-type, calm. The buddy stays steady — this is the
   blueprint's emergency guidance journey (§13D). */

export type EmergencyStep = {
  n: number;
  title: string;
  detail: string;
  /* what Sunny says when reading this step aloud */
  spoken: string;
};
export const EMERGENCY = {
  title: 'Asthma attack',
  intro: "Stay with me — we'll go through this together, one step at a time.",
  steps: [{
    n: 1,
    title: 'Sit up straight',
    detail: "Don't lie down. Sitting upright helps keep your airways open.",
    spoken: "First — sit up straight. Don't lie down. Sitting upright helps keep your airways open."
  }, {
    n: 2,
    title: 'Take your reliever',
    detail: 'One puff of your blue inhaler every 30–60 seconds, up to 10 puffs.',
    spoken: 'Now take your reliever — the blue one. One puff every 30 to 60 seconds, up to 10 puffs.'
  }, {
    n: 3,
    title: 'Breathe slowly and steadily',
    detail: "Try to keep your breathing slow and even. I'm right here with you.",
    spoken: "Keep your breathing slow and steady. You're doing well — I'm right here with you."
  }, {
    n: 4,
    title: 'No better? Repeat step 2',
    detail: "If you don't feel better after 10 puffs, take another round of puffs while help is on its way.",
    spoken: "If you don't feel better after 10 puffs, repeat the puffs — one every 30 to 60 seconds — while help is on its way."
  }, {
    n: 5,
    title: 'Call 999',
    detail: "If your reliever isn't helping, or you feel worse, call 999 now and say it's an asthma attack.",
    spoken: "If your reliever isn't helping, or you feel worse, call 999 now and tell them it's an asthma attack."
  }] as EmergencyStep[]
};